import { hashEconomicModelInput } from '../../lib/economicContribution/modelUtils';
import { policyApproveComparableProject } from '../../services/economicComparables/researchWorkflow';
import {
  ECONOMIC_CONTRIBUTION_SCHEMA_VERSION,
  type ComparableCoolingObservationValue,
  type ComparableObservation,
  type ComparableProjectRecord,
  type ComparableWaterObservationValue,
  type EconomicEvidenceBasis,
  type EconomicSourceRef,
} from '../../types/economicContribution';

const KERN_SOURCE: EconomicSourceRef = {
  id: 'kern-edc-rb-inyokern-cec-2026',
  title: 'RB Inyokern Data Center letter of support, Docket 26-SPPE-01',
  publisher: 'Kern Economic Development Corporation',
  url: 'https://efiling.energy.ca.gov/GetDocument.aspx?DocumentContentId=107381&tn=270227',
  publicationDate: '2026-05-22',
  accessedAt: '2026-08-26',
  excerpt: '99 MW; 590 peak construction workers; 30–60 permanent jobs; $250 million investment.',
  locator: 'Letter body, p. 1',
  excerptKind: 'table-transcription',
  evidenceRole: 'numeric-basis',
  notes: 'Project-specific estimates filed in a California Energy Commission docket; not verified actual employment.',
};

const TERAWULF_SOURCE: EconomicSourceRef = {
  id: 'terawulf-kentucky-psc-2026',
  title: 'Motion for Expedited Review, Kentucky PSC Case 2026-00115',
  publisher: 'TeraWulf Inc. filing in the Kentucky Public Service Commission docket',
  url: 'https://psc.ky.gov/pscecf/2026-00115/mkurtz%40bkllawfirm.com/05272026041401/TeraWulf_Motion_Exp._Review__2026-00115.pdf',
  publicationDate: '2026-05-27',
  accessedAt: '2026-08-26',
  excerpt: '482 MW; 800–1,000 peak construction jobs; 80–100 permanent data-center jobs.',
  locator: 'Motion, pp. 2 and 4',
  excerptKind: 'table-transcription',
  evidenceRole: 'numeric-basis',
  notes: 'Applicant estimates in a public utility-commission docket; not verified actual employment.',
};

const CA3_CEC_PAGE_SOURCE: EconomicSourceRef = {
  id: 'cec-ca3-project-page-2026',
  title: 'CA3 Backup Generating Facility project page',
  publisher: 'California Energy Commission',
  url: 'https://www.energy.ca.gov/powerplant/backup-generating-system/ca3-backup-generating-facility',
  accessedAt: '2026-08-26',
  excerpt: 'Project owner Vantage; docket 21-SPPE-01; capacity 96 MW; project status exempted.',
  locator: 'Project summary',
  excerptKind: 'table-transcription',
  evidenceRole: 'numeric-basis',
  notes: 'Capacity is the data-center backup/load-serving basis reported by the CEC, not IT MW.',
};

const CA3_CEC_EIR_SOURCE: EconomicSourceRef = {
  id: 'cec-ca3-final-decision-employment-2022',
  title: 'CA3 Commission Final Decision, Part 4',
  publisher: 'California Energy Commission',
  url: 'https://efiling.energy.ca.gov/GetDocument.aspx?DocumentContentId=79833&tn=245667-4',
  publicationDate: '2022-08-10',
  accessedAt: '2026-08-26',
  excerpt: 'The applicant anticipates 19–21 permanent employees, with approximately 10–14 rental-space tenant employees visiting the facility daily.',
  locator: 'Population and Housing §4.14.2, p. 4.14-4',
  excerptKind: 'verbatim-quote',
  evidenceRole: 'numeric-basis',
  notes: 'Applicant estimate adopted in the CEC decision; tenant visitors are not added to permanent onsite employment.',
};

const MCLAREN_CEC_PAGE_SOURCE: EconomicSourceRef = {
  id: 'cec-mclaren-project-page-2026',
  title: 'McLaren Backup Generating Facility project page',
  publisher: 'California Energy Commission',
  url: 'https://www.energy.ca.gov/powerplant/backup-generating-system/mclaren-backup-generating-facility',
  accessedAt: '2026-08-26',
  excerpt: 'Project owner Vantage Data Centers; docket 17-SPPE-01; capacity 98.67 MW; project status exempted.',
  locator: 'Project summary',
  excerptKind: 'table-transcription',
  evidenceRole: 'numeric-basis',
  notes: 'CEC project capacity; the proceeding included disagreement over peak-load calculation, retained as a limitation.',
};

const MCLAREN_EMPLOYMENT_SOURCE: EconomicSourceRef = {
  id: 'mclaren-initial-study-employment-2017',
  title: 'McLaren Data Center Initial Study',
  publisher: 'City of Santa Clara (CEC docket exhibit)',
  url: 'https://efiling.energy.ca.gov/GetDocument.aspx?DocumentContentId=30820&tn=222057',
  publicationDate: '2017-02-01',
  accessedAt: '2026-08-26',
  excerpt: 'Approximately 29 employees: 14 operations personnel, 13 security personnel, and two janitors.',
  locator: 'Operational Emissions, p. 74',
  excerptKind: 'verbatim-quote',
  evidenceRole: 'numeric-basis',
  notes: 'Pre-operation project estimate; not a verified actual employment count.',
};

const SEQUOIA_CEC_SOURCE: EconomicSourceRef = {
  id: 'sequoia-cec-sppe-application-2019',
  title: 'Sequoia Backup Generating Facility SPPE Application',
  publisher: 'C1-Santa Clara, LLC (California Energy Commission docket filing)',
  url: 'https://efiling.energy.ca.gov/GetDocument.aspx?DocumentContentId=60822&tn=229419-1',
  publicationDate: '2019-08-14',
  accessedAt: '2026-08-26',
  excerpt: 'Projected maximum data-center demand is 96.5 MW; a maximum of 25 full-time employees would occupy the site over each 24-hour period.',
  locator: 'Greenhouse Gas §4.8, pp. 4.8-13–4.8-14',
  excerptKind: 'table-transcription',
  evidenceRole: 'numeric-basis',
  notes: 'Applicant maximums in an SPPE application; not verified actual load or employment.',
};

const META_RICHLAND_LED_SOURCE: EconomicSourceRef = {
  id: 'led-meta-richland-expansion-2026',
  title: 'Meta commits more than $50 billion for North Louisiana project',
  publisher: 'Louisiana Economic Development',
  url: 'https://www.opportunitylouisiana.gov/news/meta-commits-more-than-50-billion-for-north-louisiana-project-becoming-one-of-the-largest-data-centers-in-history',
  publicationDate: '2026-07-13',
  accessedAt: '2026-08-26',
  excerpt: 'Nearly 10 million square feet and 5 GW of IT capacity; 7,500 peak construction jobs and approximately 1,000 operational jobs.',
  locator: 'Project summary and Creating Jobs and Local Opportunities',
  excerptKind: 'table-transcription',
  evidenceRole: 'numeric-basis',
  notes: 'Current announced expansion quantities; IT MW is not normalized to utility-service MW.',
};

const META_RICHLAND_COOLING_SOURCE: EconomicSourceRef = {
  id: 'meta-richland-closed-loop-dry-cooling-2026',
  title: 'The largest Meta data center yet brings big impact to Louisiana',
  publisher: 'Meta Data Centers',
  url: 'https://datacenters.atmeta.com/richland-parish-data-center/',
  accessedAt: '2026-09-01',
  excerpt: 'The closed-loop system circulates a glycol mixture through data halls; heat is removed with dry cooling for a majority of the year.',
  locator: 'Minimizing water use',
  excerptKind: 'table-transcription',
  evidenceRole: 'numeric-basis',
  notes: 'Project-specific cooling configuration; it does not provide a project water quantity.',
};

const GOOGLE_2025_WATER_SOURCE: EconomicSourceRef = {
  id: 'google-2025-water-by-location',
  title: '2025 Environmental Report',
  publisher: 'Google',
  url: 'https://www.gstatic.com/gumdrop/sustainability/google-2025-environmental-report.pdf',
  accessedAt: '2026-09-01',
  excerpt: '2024 table: Midlothian 221.0M gal withdrawal, 38.7M discharge, 182.3M consumption; Wilmer 1.9M, 1.7M, and 0.2M gal.',
  locator: 'Water use by data center location, pp. 110–111; endnote 177',
  excerptKind: 'table-transcription',
  evidenceRole: 'numeric-basis',
  notes: 'Calendar-year totals are normalized over 366 days for comparison; native annual values remain authoritative. Table is subject to limited assurance.',
};

const WILMER_DALLAS_COUNTY_SOURCE: EconomicSourceRef = {
  id: 'txdot-wilmer-dallas-county-location',
  title: 'Pleasant Run Road public meeting — Wilmer, Dallas County',
  publisher: 'Texas Department of Transportation',
  url: 'https://www.txdot.gov/projects/hearings-meetings/dallas/archive/pleasant-run.html',
  accessedAt: '2026-09-01',
  excerpt: 'The proposed project is in the city of Wilmer in Dallas County.',
  locator: 'Purpose',
  excerptKind: 'verbatim-quote',
  evidenceRole: 'independent-cross-check',
  notes: 'Supports the Wilmer-to-Dallas-County location mapping only; it does not substantiate the data-center identity or water quantities.',
};

const DALLAS_COUNTY_FIPS_SOURCE: EconomicSourceRef = {
  id: 'census-dallas-county-fips-48113',
  title: 'Dallas County, Texas Census Bureau Profile',
  publisher: 'U.S. Census Bureau',
  url: 'https://data.census.gov/profile/Dallas_County,_Texas?g=050XX00US48113',
  accessedAt: '2026-09-01',
  excerpt: 'Geographic identifier 050XX00US48113 identifies Dallas County, Texas.',
  locator: 'Profile geography identifier',
  excerptKind: 'table-transcription',
  evidenceRole: 'independent-cross-check',
  notes: 'Supports county FIPS 48113 only; it does not substantiate the facility or water quantities.',
};

const GOOGLE_MIDLOTHIAN_COOLING_SOURCE: EconomicSourceRef = {
  id: 'google-midlothian-evaporative-cooling-gallery',
  title: 'Data center photo gallery — Ellis County, Texas',
  publisher: 'Google Data Centers',
  url: 'https://datacenters.google/discover-more/photo-gallery/',
  accessedAt: '2026-09-01',
  excerpt: 'Google identifies evaporative cooling towers and ten modular cooling plants at its Midlothian data center campus in Ellis County.',
  locator: 'Ellis County, Texas photo captions',
  excerptKind: 'table-transcription',
  evidenceRole: 'numeric-basis',
  notes: 'Project-specific infrastructure description; it does not allocate the annual campus water total by end use.',
};

const META_2025_FORT_WORTH_WATER_SOURCE: EconomicSourceRef = {
  id: 'meta-2025-fort-worth-water-withdrawal',
  title: '2025 Environmental Data Index',
  publisher: 'Meta',
  url: 'https://sustainability.atmeta.com/wp-content/uploads/2025/10/Meta_2025-Environmental-Data-Index.pdf',
  accessedAt: '2026-09-01',
  excerpt: 'Water withdrawal by facility lists Fort Worth, Texas at 311 megaliters for 2024.',
  locator: '§3.1 Water Withdrawal, facility table, p. I (PDF p. 8)',
  excerptKind: 'table-transcription',
  evidenceRole: 'numeric-basis',
  notes: 'Calendar-year withdrawal is normalized over 366 days. The report does not publish facility-level consumption or a Fort Worth-specific cooling taxonomy.',
};

const EL_PASO_WATER_ALLOCATION_SOURCE: EconomicSourceRef = {
  id: 'el-paso-meta-tier-iii-water-allocation-2026',
  title: 'Draft Data Center Policy Framework',
  publisher: 'City of El Paso',
  url: 'https://www.elpasotexas.gov/assets/Documents/CoEP/Data-Centers/Draft-Data-Center-Policy-Framework-updated.pdf',
  accessedAt: '2026-09-01',
  excerpt: 'For Meta, the Tier III full-implementation allocation is 2.5 million gallons per day maximum and 1.5 million gallons per day average.',
  locator: 'Water discussion, footnote 29, p. 31',
  excerptKind: 'table-transcription',
  evidenceRole: 'numeric-basis',
  notes: 'A city-described full-implementation service allocation, not measured use or a cooling-only quantity.',
};

const META_EL_PASO_COOLING_SOURCE: EconomicSourceRef = {
  id: 'meta-el-paso-closed-loop-liquid-cooling-2026',
  title: 'Partnering with Texas — El Paso Data Center',
  publisher: 'Meta Data Centers',
  url: 'https://datacenters.atmeta.com/wp-content/uploads/2026/03/Meta-El-Paso-Data-Center.pdf',
  publicationDate: '2026-03-26',
  accessedAt: '2026-09-01',
  excerpt: 'The El Paso data center will use an efficient closed-loop, liquid-cooled system with zero cooling water for a majority of the year.',
  locator: 'Protecting and restoring water',
  excerptKind: 'table-transcription',
  evidenceRole: 'numeric-basis',
  notes: 'Project-specific design statement; “majority of the year” is not converted into an annual water quantity.',
};

const SHREVEPORT_TXLAAR_WATER_SOURCE: EconomicSourceRef = {
  id: 'shreveport-resolution-147-txlaar-water-2025',
  title: 'Resolution No. 147 of 2025 — TXLAAR Water and Sewer Services Agreement',
  publisher: 'City of Shreveport legal notice',
  url: 'https://louisianapublicnotice.com/notices/573061',
  publicationDate: '2025-12-09',
  accessedAt: '2026-09-01',
  excerpt: 'The campus anticipates up to 7.5 MGD potable water and up to 2.5 MGD combined wastewater and cooling-water blowdown.',
  locator: 'Resolution No. 147 of 2025, recitals',
  excerptKind: 'table-transcription',
  evidenceRole: 'numeric-basis',
  notes: 'Legally noticed service ceilings, not actual, average-day, consumption, or cooling-only use. The notice does not identify the ultimate operator.',
};

function observation<T>(
  value: T,
  source: EconomicSourceRef,
  phaseBasis: string,
): ComparableObservation<T> {
  return {
    value,
    evidenceBasis: 'announced',
    sources: [source],
    asOf: source.publicationDate,
    phaseBasis,
    limitations: ['Applicant or economic-development estimate; not a verified operating outcome.'],
  };
}

function structuredObservation<T extends ComparableWaterObservationValue | ComparableCoolingObservationValue>(
  observationId: string,
  value: T,
  source: EconomicSourceRef | readonly EconomicSourceRef[],
  phaseBasis: string,
  evidenceBasis: Exclude<EconomicEvidenceBasis, 'modelled' | 'operator-provided'>,
  evidenceAuthority: NonNullable<ComparableObservation<T>['evidenceAuthority']>,
  asOf: string,
  limitations: readonly string[],
): ComparableObservation<T> {
  return {
    observationId,
    value,
    evidenceBasis,
    evidenceAuthority,
    sources: Array.isArray(source) ? source : [source],
    asOf,
    phaseBasis,
    limitations,
  };
}

const PROPOSED_COMPARABLE_PROJECTS: readonly ComparableProjectRecord[] = [
  {
    schemaVersion: ECONOMIC_CONTRIBUTION_SCHEMA_VERSION,
    projectId: 'rb-inyokern-ca-2026',
    facilityName: 'RB Inyokern Data Center',
    projectEntity: 'RB Inyokern LLC',
    location: { stateFips: '06', countyFips: '06029', stateCode: 'CA', locality: 'Unincorporated Kern County' },
    status: 'proposed',
    operatingModel: 'unknown',
    workload: 'ai-hpc',
    mw: observation({ value: 99, basis: 'utility-service', phaseBasis: 'proposed-campus' }, KERN_SOURCE, 'proposed-campus'),
    capitalInvestmentUsd: observation(250_000_000, KERN_SOURCE, 'proposed-campus'),
    constructionPeakWorkers: observation(590, KERN_SOURCE, 'proposed-campus'),
    permanentJobsRange: observation({ low: 30, high: 60 }, KERN_SOURCE, 'proposed-campus'),
    reviewStatus: 'proposed',
    proposedBy: 'engineering-public-record-seed',
    proposedAt: '2026-08-26',
    confidence: 'medium',
    limitations: ['All employment quantities are announced/estimated; no operating compliance record exists.'],
  },
  {
    schemaVersion: ECONOMIC_CONTRIBUTION_SCHEMA_VERSION,
    projectId: 'justified-datapower-hawesville-ky-2026',
    facilityName: 'Justified DataPower Hawesville',
    operator: 'TeraWulf Inc.',
    projectEntity: 'Justified DataPower LLC',
    location: { stateFips: '21', countyFips: '21091', stateCode: 'KY', locality: 'Hawesville' },
    status: 'proposed',
    operatingModel: 'hyperscale',
    workload: 'ai-hpc',
    mw: observation({ value: 482, basis: 'utility-service', phaseBasis: 'full-load-2027' }, TERAWULF_SOURCE, 'full-load-2027'),
    capitalInvestmentUsdRange: observation({ low: 13_500_000_000, high: 14_000_000_000 }, TERAWULF_SOURCE, 'full-load-2027'),
    constructionPeakWorkersRange: observation({ low: 800, high: 1_000 }, TERAWULF_SOURCE, 'full-load-2027'),
    permanentJobsRange: observation({ low: 80, high: 100 }, TERAWULF_SOURCE, 'full-load-2027'),
    reviewStatus: 'proposed',
    proposedBy: 'engineering-public-record-seed',
    proposedAt: '2026-08-26',
    confidence: 'medium',
    limitations: ['The motion reports projected figures and requested expedited approval; values are not verified actual outcomes.'],
  },
  {
    schemaVersion: ECONOMIC_CONTRIBUTION_SCHEMA_VERSION,
    projectId: 'vantage-ca3-santa-clara-ca',
    facilityName: 'Vantage CA3 Data Center',
    operator: 'Vantage Data Centers',
    location: { stateFips: '06', countyFips: '06085', stateCode: 'CA', locality: 'Santa Clara' },
    status: 'unknown',
    operatingModel: 'colocation',
    workload: 'unknown',
    mw: observation({ value: 96, basis: 'backup-generation', phaseBasis: 'approved-project' }, CA3_CEC_PAGE_SOURCE, 'approved-project'),
    permanentJobsRange: observation({ low: 19, high: 21 }, CA3_CEC_EIR_SOURCE, 'approved-project'),
    reviewStatus: 'proposed',
    proposedBy: 'engineering-public-record-seed',
    proposedAt: '2026-08-26',
    confidence: 'medium',
    limitations: [
      'CEC project-page capacity is backup-generation nameplate, not facility, utility-service, or IT demand.',
      'CEC exemption does not by itself verify that the facility is operating or that projected employment was achieved.',
    ],
  },
  {
    schemaVersion: ECONOMIC_CONTRIBUTION_SCHEMA_VERSION,
    projectId: 'vantage-mclaren-santa-clara-ca',
    facilityName: 'Vantage McLaren Data Center',
    operator: 'Vantage Data Centers',
    location: { stateFips: '06', countyFips: '06085', stateCode: 'CA', locality: 'Santa Clara' },
    status: 'unknown',
    operatingModel: 'colocation',
    workload: 'unknown',
    mw: observation({ value: 98.67, basis: 'backup-generation', phaseBasis: 'approved-project' }, MCLAREN_CEC_PAGE_SOURCE, 'approved-project'),
    permanentJobs: observation(29, MCLAREN_EMPLOYMENT_SOURCE, 'approved-project'),
    reviewStatus: 'proposed',
    proposedBy: 'engineering-public-record-seed',
    proposedAt: '2026-08-26',
    confidence: 'medium',
    limitations: [
      'CEC project-page capacity is backup-generation nameplate and is not compatible with a jobs-per-demand-MW calculation.',
      'MW and employment come from separate records in the same project docket; both are pre-operation estimates.',
    ],
  },
  {
    schemaVersion: ECONOMIC_CONTRIBUTION_SCHEMA_VERSION,
    projectId: 'sequoia-santa-clara-ca',
    facilityName: 'Sequoia Data Center',
    projectEntity: 'C1-Santa Clara, LLC',
    location: { stateFips: '06', countyFips: '06085', stateCode: 'CA', locality: 'Santa Clara' },
    status: 'unknown',
    operatingModel: 'unknown',
    workload: 'unknown',
    mw: observation({ value: 96.5, basis: 'utility-service', phaseBasis: 'application-project' }, SEQUOIA_CEC_SOURCE, 'application-project'),
    permanentJobs: observation(25, SEQUOIA_CEC_SOURCE, 'application-project'),
    reviewStatus: 'proposed',
    proposedBy: 'engineering-public-record-seed',
    proposedAt: '2026-08-26',
    confidence: 'medium',
    limitations: ['The 25-job observation is an applicant maximum, not a verified operating headcount.'],
  },
  {
    schemaVersion: ECONOMIC_CONTRIBUTION_SCHEMA_VERSION,
    projectId: 'meta-richland-parish-la-2026',
    facilityName: 'Meta Richland Parish Data Center',
    operator: 'Meta',
    location: { stateFips: '22', countyFips: '22083', stateCode: 'LA', locality: 'Holly Ridge' },
    status: 'construction',
    operatingModel: 'hyperscale',
    workload: 'ai-hpc',
    mw: observation({ value: 5_000, basis: 'it', phaseBasis: 'expanded-campus-2026' }, META_RICHLAND_LED_SOURCE, 'expanded-campus-2026'),
    constructionPeakWorkers: observation(7_500, META_RICHLAND_LED_SOURCE, 'expanded-campus-2026'),
    permanentJobs: observation(1_000, META_RICHLAND_LED_SOURCE, 'expanded-campus-2026'),
    coolingObservations: [structuredObservation(
      'meta-richland-closed-loop-dry-cooling-2026',
      {
        kind: 'cooling',
        rackCoolingStrategy: 'liquid',
        heatRejection: 'dry-cooler',
        scope: 'campus',
        nativeDescription: 'Closed-loop glycol cooling with dry heat rejection for a majority of the year.',
      },
      META_RICHLAND_COOLING_SOURCE,
      'expanded-campus-design-2026',
      'announced',
      'operator-primary',
      '2026-09-01',
      ['No project-specific annual water quantity or full-duty dry-cooler capacity curve is published in this record.'],
    )],
    reviewStatus: 'proposed',
    proposedBy: 'engineering-public-record-seed',
    proposedAt: '2026-08-26',
    confidence: 'medium',
    limitations: ['The announced 5 GW denominator is IT capacity and must not be normalized against utility-service MW.'],
  },
  {
    schemaVersion: ECONOMIC_CONTRIBUTION_SCHEMA_VERSION,
    projectId: 'google-midlothian-tx-operating-2024',
    facilityName: 'Google Midlothian Data Center',
    operator: 'Google',
    location: { stateFips: '48', countyFips: '48139', stateCode: 'TX', locality: 'Midlothian' },
    status: 'operating',
    operatingModel: 'hyperscale',
    workload: 'unknown',
    waterTimeline: [
      structuredObservation('google-midlothian-withdrawal-2024', {
        kind: 'water', stage: 'reported-actual', flow: 'withdrawal', amount: 221, units: 'million-gallons',
        normalizedGallonsPerDay: 603_825.14, statistic: 'annual-total', boundary: 'campus', waterSource: 'unknown',
        periodStart: '2024-01-01', periodEnd: '2024-12-31',
      }, GOOGLE_2025_WATER_SOURCE, 'operating-location-2024', 'verified-actual', 'operator-primary', '2024-12-31', [
        'Total campus withdrawal; the report does not allocate this value to cooling versus ancillary site uses.',
      ]),
      structuredObservation('google-midlothian-consumption-2024', {
        kind: 'water', stage: 'reported-actual', flow: 'consumption', amount: 182.3, units: 'million-gallons',
        normalizedGallonsPerDay: 498_087.43, statistic: 'annual-total', boundary: 'campus', waterSource: 'unknown',
        periodStart: '2024-01-01', periodEnd: '2024-12-31',
      }, GOOGLE_2025_WATER_SOURCE, 'operating-location-2024', 'verified-actual', 'operator-primary', '2024-12-31', [
        'Total campus consumption; the report does not allocate this value to cooling versus ancillary site uses.',
      ]),
      structuredObservation('google-midlothian-discharge-2024', {
        kind: 'water', stage: 'reported-actual', flow: 'discharge', amount: 38.7, units: 'million-gallons',
        normalizedGallonsPerDay: 105_737.7, statistic: 'annual-total', boundary: 'campus', waterSource: 'unknown',
        periodStart: '2024-01-01', periodEnd: '2024-12-31',
      }, GOOGLE_2025_WATER_SOURCE, 'operating-location-2024', 'verified-actual', 'operator-primary', '2024-12-31', [
        'Total campus discharge; subtraction checks the disclosed withdrawal/consumption boundary but is not a cooling-only flow.',
      ]),
    ],
    coolingObservations: [structuredObservation('google-midlothian-evaporative-towers', {
      kind: 'cooling', heatRejection: 'wet-cooling-tower', scope: 'campus',
      nativeDescription: 'Evaporative cooling towers and modular cooling plants are documented at the campus.',
    }, GOOGLE_MIDLOTHIAN_COOLING_SOURCE, 'operating-campus', 'observed-public-record', 'operator-primary', '2026-09-01', [
      'The public photo captions establish infrastructure type, not operating hours or the cooling share of total campus water.',
    ])],
    reviewStatus: 'proposed',
    proposedBy: 'engineering-public-record-seed',
    proposedAt: '2026-09-01',
    confidence: 'high',
    limitations: ['No comparable project MW is published in the cited records; water-per-MW normalization is withheld.'],
  },
  {
    schemaVersion: ECONOMIC_CONTRIBUTION_SCHEMA_VERSION,
    projectId: 'google-wilmer-tx-operating-2024',
    facilityName: 'Google Wilmer Data Center',
    operator: 'Google',
    location: { stateFips: '48', countyFips: '48113', stateCode: 'TX', locality: 'Wilmer' },
    status: 'operating',
    operatingModel: 'hyperscale',
    workload: 'unknown',
    waterTimeline: [
      structuredObservation('google-wilmer-withdrawal-2024', {
        kind: 'water', stage: 'reported-actual', flow: 'withdrawal', amount: 1.9, units: 'million-gallons',
        normalizedGallonsPerDay: 5_191.26, statistic: 'annual-total', boundary: 'campus', waterSource: 'unknown',
        periodStart: '2024-01-01', periodEnd: '2024-12-31',
      }, [GOOGLE_2025_WATER_SOURCE, WILMER_DALLAS_COUNTY_SOURCE, DALLAS_COUNTY_FIPS_SOURCE], 'operating-location-2024', 'verified-actual', 'operator-primary', '2024-12-31', [
        'Total campus withdrawal; MW and the share attributable to cooling are not published.',
      ]),
      structuredObservation('google-wilmer-consumption-2024', {
        kind: 'water', stage: 'reported-actual', flow: 'consumption', amount: 0.2, units: 'million-gallons',
        normalizedGallonsPerDay: 546.45, statistic: 'annual-total', boundary: 'campus', waterSource: 'unknown',
        periodStart: '2024-01-01', periodEnd: '2024-12-31',
      }, [GOOGLE_2025_WATER_SOURCE, WILMER_DALLAS_COUNTY_SOURCE, DALLAS_COUNTY_FIPS_SOURCE], 'operating-location-2024', 'verified-actual', 'operator-primary', '2024-12-31', [
        'Total campus consumption; MW and the share attributable to cooling are not published.',
      ]),
      structuredObservation('google-wilmer-discharge-2024', {
        kind: 'water', stage: 'reported-actual', flow: 'discharge', amount: 1.7, units: 'million-gallons',
        normalizedGallonsPerDay: 4_644.81, statistic: 'annual-total', boundary: 'campus', waterSource: 'unknown',
        periodStart: '2024-01-01', periodEnd: '2024-12-31',
      }, [GOOGLE_2025_WATER_SOURCE, WILMER_DALLAS_COUNTY_SOURCE, DALLAS_COUNTY_FIPS_SOURCE], 'operating-location-2024', 'verified-actual', 'operator-primary', '2024-12-31', [
        'Total campus discharge; MW and the share attributable to cooling are not published.',
      ]),
    ],
    coolingObservations: [structuredObservation('google-wilmer-air-cooled-2024', {
      kind: 'cooling', scope: 'campus', nativeDescription: 'Google identifies Wilmer as an air-cooled facility.',
    }, [GOOGLE_2025_WATER_SOURCE, WILMER_DALLAS_COUNTY_SOURCE, DALLAS_COUNTY_FIPS_SOURCE], 'operating-location-2024', 'verified-actual', 'operator-primary', '2024-12-31', [
      'The report does not map “air-cooled” to a more specific rack, plant, or heat-rejection axis.',
    ])],
    reviewStatus: 'proposed',
    proposedBy: 'engineering-public-record-seed',
    proposedAt: '2026-09-01',
    confidence: 'high',
    limitations: ['No comparable project MW is published in the cited record; water-per-MW normalization is withheld.'],
  },
  {
    schemaVersion: ECONOMIC_CONTRIBUTION_SCHEMA_VERSION,
    projectId: 'meta-fort-worth-tx-operating-2024',
    facilityName: 'Meta Fort Worth Data Center',
    operator: 'Meta',
    location: { stateFips: '48', countyFips: '48439', stateCode: 'TX', locality: 'Fort Worth' },
    status: 'operating',
    operatingModel: 'hyperscale',
    workload: 'unknown',
    waterTimeline: [structuredObservation('meta-fort-worth-withdrawal-2024', {
      kind: 'water', stage: 'reported-actual', flow: 'withdrawal', amount: 311_000_000, units: 'liters',
      normalizedGallonsPerDay: 224_474.07, statistic: 'annual-total', boundary: 'campus', waterSource: 'unknown',
      periodStart: '2024-01-01', periodEnd: '2024-12-31',
    }, META_2025_FORT_WORTH_WATER_SOURCE, 'operating-location-2024', 'observed-public-record', 'operator-primary', '2024-12-31', [
      'Facility withdrawal only; facility-level consumption, discharge, cooling allocation, and capacity MW are not published in this table.',
    ])],
    reviewStatus: 'proposed',
    proposedBy: 'engineering-public-record-seed',
    proposedAt: '2026-09-01',
    confidence: 'high',
    limitations: ['Electricity consumption is not treated as installed or contracted MW capacity.'],
  },
  {
    schemaVersion: ECONOMIC_CONTRIBUTION_SCHEMA_VERSION,
    projectId: 'meta-el-paso-tx-full-campus',
    facilityName: 'Meta El Paso Data Center',
    operator: 'Meta',
    location: { stateFips: '48', countyFips: '48141', stateCode: 'TX', locality: 'El Paso' },
    status: 'construction',
    operatingModel: 'hyperscale',
    workload: 'ai-hpc',
    waterTimeline: [
      structuredObservation('meta-el-paso-tier-iii-average-allocation', {
        kind: 'water', stage: 'permit-limit', flow: 'withdrawal', amount: 1.5, units: 'million-gal/day',
        normalizedGallonsPerDay: 1_500_000, statistic: 'average-day', boundary: 'campus', waterSource: 'municipal',
        permitOrApplicationId: 'City of El Paso Tier III municipal water service allocation',
      }, EL_PASO_WATER_ALLOCATION_SOURCE, 'tier-iii-full-implementation', 'committed', 'government-record', '2026-07-01', [
        'Contractual municipal-water service allocation, not an environmental water permit, expected cooling use, or measured operation.',
      ]),
      structuredObservation('meta-el-paso-tier-iii-maximum-allocation', {
        kind: 'water', stage: 'permit-limit', flow: 'withdrawal', amount: 2.5, units: 'million-gal/day',
        normalizedGallonsPerDay: 2_500_000, statistic: 'maximum-day', boundary: 'campus', waterSource: 'municipal',
        permitOrApplicationId: 'City of El Paso Tier III municipal water service allocation',
      }, EL_PASO_WATER_ALLOCATION_SOURCE, 'tier-iii-full-implementation', 'committed', 'government-record', '2026-07-01', [
        'Contractual municipal-water service ceiling, not an environmental water permit, expected cooling use, or measured operation.',
      ]),
    ],
    coolingObservations: [structuredObservation('meta-el-paso-closed-loop-liquid-cooling', {
      kind: 'cooling', rackCoolingStrategy: 'liquid', scope: 'campus',
      nativeDescription: 'Closed-loop liquid cooling recirculates the same fluid and is described as using zero cooling water for a majority of the year.',
    }, META_EL_PASO_COOLING_SOURCE, 'full-campus-design', 'announced', 'operator-primary', '2026-03-26', [
      'The operator statement does not quantify the wet-assist fraction or total annual site water.',
    ])],
    reviewStatus: 'proposed',
    proposedBy: 'engineering-public-record-seed',
    proposedAt: '2026-09-01',
    confidence: 'medium',
    limitations: ['Service allocation and operating water must remain separate; the data center is not yet an operating actual.'],
  },
  {
    schemaVersion: ECONOMIC_CONTRIBUTION_SCHEMA_VERSION,
    projectId: 'txlaar-shreveport-la-resolution-147',
    facilityName: 'TXLAAR Shreveport Data Center Campus',
    projectEntity: 'TXLAAR, LLC',
    location: { stateFips: '22', countyFips: '22017', stateCode: 'LA', locality: 'Shreveport' },
    status: 'proposed',
    operatingModel: 'unknown',
    workload: 'unknown',
    waterTimeline: [
      structuredObservation('txlaar-shreveport-water-service-limit', {
        kind: 'water', stage: 'permit-limit', flow: 'withdrawal', amount: 7.5, units: 'million-gal/day',
        normalizedGallonsPerDay: 7_500_000, statistic: 'unspecified', boundary: 'campus', waterSource: 'municipal',
        permitOrApplicationId: 'Shreveport Resolution No. 147 of 2025',
      }, SHREVEPORT_TXLAAR_WATER_SOURCE, 'proposed-full-campus', 'committed', 'government-record', '2025-12-18', [
        'Potable service ceiling; not average-day demand, consumption, cooling-only use, or an operating actual.',
      ]),
      structuredObservation('txlaar-shreveport-combined-discharge-limit', {
        kind: 'water', stage: 'permit-limit', flow: 'discharge', amount: 2.5, units: 'million-gal/day',
        normalizedGallonsPerDay: 2_500_000, statistic: 'unspecified', boundary: 'campus', waterSource: 'municipal',
        permitOrApplicationId: 'Shreveport Resolution No. 147 of 2025',
      }, SHREVEPORT_TXLAAR_WATER_SOURCE, 'proposed-full-campus', 'committed', 'government-record', '2025-12-18', [
        'Combined domestic wastewater and cooling-water blowdown ceiling; not cooling consumption or an operating actual.',
      ]),
    ],
    coolingObservations: [structuredObservation('txlaar-shreveport-cooling-water-blowdown', {
      kind: 'cooling', scope: 'campus',
      nativeDescription: 'The service agreement identifies cooling-water supply and combined wastewater/cooling-water blowdown; exact cooling equipment is not specified.',
    }, SHREVEPORT_TXLAAR_WATER_SOURCE, 'proposed-full-campus', 'committed', 'government-record', '2025-12-18', [
      'Cooling technology and the cooling share of the service ceilings are not established by the legal notice.',
    ])],
    reviewStatus: 'proposed',
    proposedBy: 'engineering-public-record-seed',
    proposedAt: '2026-09-01',
    confidence: 'medium',
    limitations: ['The public notice does not identify the ultimate operator; no operator identity is inferred.'],
  },
];

/** Source-visible public records; matching code enforces MW-basis and phase compatibility. */
export const APPROVED_COMPARABLE_PROJECTS: readonly ComparableProjectRecord[] = PROPOSED_COMPARABLE_PROJECTS.map((record) => (
  policyApproveComparableProject(record, {
    validatedAt: '2026-08-26',
    sourceRecordHash: hashEconomicModelInput({ record }),
  })
));
